import { useState, useRef, useCallback, useEffect, useLayoutEffect } from "react";
import { cn } from "@repo/utils";

type Props = {
  content: React.ReactNode;
  children: React.ReactNode;
  className?: string;
  delay?: number;
};

export const HoverTooltip = ({ content, children, className, delay = 400 }: Props) => {
  const [isVisible, setIsVisible] = useState(false);
  const [position, setPosition] = useState<"center" | "left" | "right">("center");
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const tooltipRef = useRef<HTMLSpanElement>(null);

  const show = useCallback(() => {
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    timeoutRef.current = setTimeout(() => setIsVisible(true), delay);
  }, [delay]);

  const hide = useCallback(() => {
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    setIsVisible(false);
    setPosition("center");
  }, []);

  useEffect(() => {
    return () => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
    };
  }, []);

  useLayoutEffect(() => {
    if (!isVisible || !tooltipRef.current) return;
    const rect = tooltipRef.current.getBoundingClientRect();
    if (rect.left < 4) {
      setPosition("left");
    } else if (rect.right > window.innerWidth - 4) {
      setPosition("right");
    }
  }, [isVisible]);

  return (
    <span
      className="relative inline-flex"
      onMouseEnter={show}
      onMouseLeave={hide}
      onFocus={show}
      onBlur={hide}
    >
      {children}
      {isVisible && content && (
        <span
          ref={tooltipRef}
          role="tooltip"
          className={cn(
            "absolute bottom-full z-50 mb-1.5 rounded bg-gray-900 px-2 py-1 text-xs whitespace-nowrap text-white shadow-lg",
            {
              "left-1/2 -translate-x-1/2": position === "center",
              "left-0": position === "left",
              "right-0": position === "right",
            },
            className
          )}
        >
          {content}
        </span>
      )}
    </span>
  );
};
